import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { logoAPI, resolveLogoUrl } from '../../api/logo';

export default function LoginPage() {
  const { login, loading } = useAuth();
  const [serverError, setServerError] = useState('');
  const [logoUrl,     setLogoUrl]     = useState(null);

  useEffect(() => {
    logoAPI.get()
      .then(({ data }) => { if (data.status && data.data) setLogoUrl(resolveLogoUrl(data.data.logo_url)); })
      .catch(() => {});
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setServerError('');
    try {
      const res = await login(data);
      if (!res.status) setServerError(res.message || 'Login failed');
    } catch (err) {
      setServerError(err.response?.data?.message || 'Something went wrong');
    }
  };

  return (
    <div className="app-shell flex flex-col">
      {/* Header brand */}
      <div className="flex flex-col items-center justify-center pt-16 pb-8 px-6">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="w-20 h-20 rounded-3xl flex items-center justify-center mb-5 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
        >
          {logoUrl ? (
            <img src={logoUrl} alt="Shine Jewellery" className="w-full h-full object-contain p-1" />
          ) : (
            <span className="text-white font-serif font-bold text-3xl">S</span>
          )}
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="text-2xl font-serif font-bold text-white"
        >
          Shine Jewellery
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="text-sm mt-1"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          Premium Investment Platform
        </motion.p>
      </div>

      {/* Form card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="flex-1 mx-4 rounded-3xl p-6 border"
        style={{
          background: 'rgba(255,255,255,0.07)',
          borderColor: 'rgba(212,160,23,0.22)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
        }}
      >
        <h2 className="text-xl font-serif font-semibold text-white mb-1">Welcome back</h2>
        <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>Sign in to your account</p>

        {serverError && (
          <div
            className="mb-4 rounded-xl border px-4 py-3 text-sm"
            style={{ background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.3)', color: '#fca5a5' }}
          >
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Input
            label="Mobile Number"
            type="tel"
            placeholder="Enter your mobile number"
            error={errors.mobile?.message}
            {...register('mobile', {
              required: 'Mobile number is required',
              pattern: { value: /^\d{10}$/, message: 'Enter a valid 10-digit number' },
            })}
          />

          <Input
            label="Password"
            type="password"
            placeholder="Enter your password"
            error={errors.password?.message}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Minimum 6 characters' },
            })}
          />

          <Button type="submit" loading={loading} fullWidth className="mt-2">
            Sign In
          </Button>
        </form>

        <div className="divider my-6">or</div>

        <p className="text-center text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
          Don't have an account?{' '}
          <Link to="/register" className="font-semibold" style={{ color: '#f0c040' }}>
            Create account
          </Link>
        </p>
      </motion.div>

      <div className="py-6 text-center text-xs space-y-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>
        <p>© 2026 Shine Jewellery</p>
        <p>
          <Link
            to="/policies/terms"
            className="underline underline-offset-2 hover:opacity-80 transition-opacity"
            style={{ color: 'rgba(212,160,23,0.6)' }}
          >
            Terms
          </Link>
          {' · '}
          <Link
            to="/policies/refund"
            className="underline underline-offset-2 hover:opacity-80 transition-opacity"
            style={{ color: 'rgba(212,160,23,0.6)' }}
          >
            Refund Policy
          </Link>
          {' · '}
          <Link
            to="/policies/privacy"
            className="underline underline-offset-2 hover:opacity-80 transition-opacity"
            style={{ color: 'rgba(212,160,23,0.6)' }}
          >
            Privacy
          </Link>
          {' · '}
          <Link
            to="/policies/shipping"
            className="underline underline-offset-2 hover:opacity-80 transition-opacity"
            style={{ color: 'rgba(212,160,23,0.6)' }}
          >
            Delivery
          </Link>
          {' · '}
          <Link
            to="/contact"
            className="underline underline-offset-2 hover:opacity-80 transition-opacity"
            style={{ color: 'rgba(212,160,23,0.6)' }}
          >
            Contact Us
          </Link>
        </p>
      </div>
    </div>
  );
}
